// Tabs reducer for managing multiple declaration tabs Each tab has its own isolated data to prevent unnecessary re-renders

// Action Types
export const CREATE_TAB = "CREATE_TAB";
export const DELETE_TAB = "DELETE_TAB";
export const SWITCH_TAB = "SWITCH_TAB";
export const UPDATE_TAB_ORDER = "UPDATE_TAB_ORDER";
export const UPDATE_TAB_EDITABLE_DATA = "UPDATE_TAB_EDITABLE_DATA";
export const UPDATE_TAB_VALIDATION_ERRORS = "UPDATE_TAB_VALIDATION_ERRORS";
export const UPDATE_TAB_TARIC_ERRORS = "UPDATE_TAB_TARIC_ERRORS";
export const UPDATE_TAB_SELECTED_AGENT = "UPDATE_TAB_SELECTED_AGENT";
export const UPDATE_TAB_SELECTED_SENDER = "UPDATE_TAB_SELECTED_SENDER";
export const UPDATE_TAB_SELECTED_RECEIVER = "UPDATE_TAB_SELECTED_RECEIVER";
export const MIGRATE_ARRAY_TO_TABS = "MIGRATE_ARRAY_TO_TABS";
export const RESET_TABS = "RESET_TABS";
export const UPDATE_TAB_STATUS = "UPDATE_TAB_STATUS";
export const UPDATE_TAB_IS_DIRTY = "UPDATE_TAB_IS_DIRTY";

// Initial state
const initialTabsState = {
  tabs: {}, // { [tabId]: { tabId, declarationIndex, selectedAgent, selectedSender, selectedReceiver, editableData, validationErrors, taricJsonErrors, isDirty, status, createdAt, label } }
  activeTabId: null,
  tabOrder: [], // Array of tabIds to maintain order
  nextTabIndex: 1, // For generating sequential tab IDs
};

// Helper to generate unique tab ID - simple index-based naming
export const generateTabId = (index) => {
  return `tab-${index + 1}`;
};

// Tabs reducer
const tabsReducer = (state = initialTabsState, action) => {
  switch (action.type) {
    case CREATE_TAB: {
      const { tabId, declarationIndex, initialData = {} } = action.payload;
      if (!tabId) {
        console.warn('CREATE_TAB: tabId is required');
        return state;
      }
      
      // Prevent duplicate tab creation - if tab already exists, just return state
      if (state.tabs[tabId]) {
        console.warn(`CREATE_TAB: Tab ${tabId} already exists, skipping duplicate creation`);
        return state;
      }

      const newTab = {
        tabId,
        declarationIndex: declarationIndex !== undefined ? declarationIndex : null,
        selectedAgent: initialData.selectedAgent || null,
        selectedSender: initialData.selectedSender || null,
        selectedReceiver: initialData.selectedReceiver || null,
        editableData: initialData.editableData || null,
        validationErrors: initialData.validationErrors || null,
        taricJsonErrors: initialData.taricJsonErrors || null,
        isDirty: initialData.isDirty || false,
        status: initialData.status || 'draft',
        createdAt: Date.now(),
        label: initialData.label || `Export #${state.nextTabIndex}`,
      };

      return {
        ...state,
        tabs: {
          ...state.tabs,
          [tabId]: newTab,
        },
        tabOrder: [...state.tabOrder, tabId],
        activeTabId: state.activeTabId !== null ? state.activeTabId : null, // Keep null (All view) as default, or use existing activeTabId
        nextTabIndex: state.nextTabIndex + 1,
      };
    }

    case DELETE_TAB: {
      const { tabId } = action.payload;
      if (!tabId || !state.tabs[tabId]) {
        return state;
      }

      // Prevent deleting the last tab - always keep at least one
      if (Object.keys(state.tabs).length <= 1) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('DELETE_TAB: Cannot delete the last tab. At least one tab must remain.');
        }
        return state;
      }

      const newTabs = { ...state.tabs };
      delete newTabs[tabId];

      const newTabOrder = state.tabOrder.filter(id => id !== tabId);
      
      // If deleted tab was active, switch to first remaining tab
      let newActiveTabId = state.activeTabId;
      if (state.activeTabId === tabId) {
        newActiveTabId = newTabOrder.length > 0 ? newTabOrder[0] : null;
      }

      return {
        ...state,
        tabs: newTabs,
        tabOrder: newTabOrder,
        activeTabId: newActiveTabId,
      };
    }

    case SWITCH_TAB: {
      const { tabId } = action.payload;
      // Allow null to represent "All" view (show all tabs)
      if (tabId === null) {
        return {
          ...state,
          activeTabId: null,
        };
      }
      // Validate tabId exists if not null
      if (!tabId || !state.tabs[tabId]) {
        console.warn('SWITCH_TAB: Invalid tabId', tabId);
        return state;
      }

      return {
        ...state,
        activeTabId: tabId,
      };
    }

    case UPDATE_TAB_ORDER: {
      const { newOrder } = action.payload;
      if (!Array.isArray(newOrder)) {
        return state;
      }

      // Validate that all tabIds in newOrder exist
      const validOrder = newOrder.filter(tabId => state.tabs[tabId]);
      if (validOrder.length !== newOrder.length) {
        console.warn('UPDATE_TAB_ORDER: Some tabIds in newOrder do not exist');
      }

      return {
        ...state,
        tabOrder: validOrder,
      };
    }

    case UPDATE_TAB_EDITABLE_DATA: {
      const { tabId, data } = action.payload;
      if (!tabId || !state.tabs[tabId]) {
        return state;
      }

      return {
        ...state,
        tabs: {
          ...state.tabs,
          [tabId]: {
            ...state.tabs[tabId],
            editableData: data,
          },
        },
      };
    }

    case UPDATE_TAB_VALIDATION_ERRORS: {
      const { tabId, errors } = action.payload;
      if (!tabId || !state.tabs[tabId]) {
        return state;
      }

      return {
        ...state,
        tabs: {
          ...state.tabs,
          [tabId]: {
            ...state.tabs[tabId],
            validationErrors: errors,
          },
        },
      };
    }

    case UPDATE_TAB_TARIC_ERRORS: {
      const { tabId, errors } = action.payload;
      if (!tabId || !state.tabs[tabId]) {
        return state;
      }

      return {
        ...state,
        tabs: {
          ...state.tabs,
          [tabId]: {
            ...state.tabs[tabId],
            taricJsonErrors: errors,
          },
        },
      };
    }

    case UPDATE_TAB_SELECTED_AGENT: {
      const { tabId, agent } = action.payload;
      if (!tabId || !state.tabs[tabId]) {
        return state;
      }

      return {
        ...state,
        tabs: {
          ...state.tabs,
          [tabId]: {
            ...state.tabs[tabId],
            selectedAgent: agent,
          },
        },
      };
    }

    case UPDATE_TAB_SELECTED_SENDER: {
      const { tabId, sender } = action.payload;
      if (!tabId || !state.tabs[tabId]) {
        return state;
      }

      return {
        ...state,
        tabs: {
          ...state.tabs,
          [tabId]: {
            ...state.tabs[tabId],
            selectedSender: sender,
          },
        },
      };
    }

    case UPDATE_TAB_SELECTED_RECEIVER: {
      const { tabId, receiver } = action.payload;
      if (!tabId || !state.tabs[tabId]) {
        return state;
      }

      return {
        ...state,
        tabs: {
          ...state.tabs,
          [tabId]: {
            ...state.tabs[tabId],
            selectedReceiver: receiver,
          },
        },
      };
    }

    case UPDATE_TAB_STATUS: {
      const { tabId, status } = action.payload;
      if (!tabId || !state.tabs[tabId]) {
        return state;
      }

      return {
        ...state,
        tabs: {
          ...state.tabs,
          [tabId]: {
            ...state.tabs[tabId],
            status: status,
          },
        },
      };
    }

    case UPDATE_TAB_IS_DIRTY: {
      const { tabId, isDirty } = action.payload;
      if (!tabId || !state.tabs[tabId]) {
        console.warn('UPDATE_TAB_IS_DIRTY: Tab not found:', { tabId, existingTabs: Object.keys(state.tabs) });
        return state;
      }

      const previousIsDirty = state.tabs[tabId].isDirty;
      
      // Only update if value actually changed (prevent unnecessary re-renders)
      if (previousIsDirty === isDirty) {
        return state;
      }
      
      // Only log when value actually changes (avoid spam on every render)
      if (process.env.NODE_ENV === 'development') {
        console.log('🔄 UPDATE_TAB_IS_DIRTY:', {
          tabId,
          previousIsDirty,
          newIsDirty: isDirty,
          isActiveTab: tabId === state.activeTabId,
        });
      }

      return {
        ...state,
        tabs: {
          ...state.tabs,
          [tabId]: {
            ...state.tabs[tabId],
            isDirty: isDirty,
          },
        },
      };
    }

    case MIGRATE_ARRAY_TO_TABS: {
      const { extractedData, selectedAgents = [], selectedSenders = [], selectedReceivers = [], editableData = null } = action.payload;
      
      if (!extractedData) {
        return state;
      }

      const declarations = Array.isArray(extractedData) ? extractedData : [extractedData];
      if (declarations.length === 0) {
        return state;
      }

      // If tabs already exist, don't migrate
      if (Object.keys(state.tabs).length > 0) {
        return state;
      }

      const newTabs = {};
      const newTabOrder = [];
      let nextIndex = 1;

      declarations.forEach((declaration, index) => {
        const tabId = generateTabId(index);
        const editableDataForTab = editableData && Array.isArray(editableData) 
          ? editableData[index] 
          : (editableData && !Array.isArray(editableData) && index === 0 ? editableData : null);

        // Use declaration data if editableData is not available
        const tabEditableData = editableDataForTab || (Array.isArray(declaration) ? null : declaration);
        
        newTabs[tabId] = {
          tabId,
          declarationIndex: index,
          selectedAgent: selectedAgents[index] || null,
          selectedSender: selectedSenders[index] || null,
          selectedReceiver: selectedReceivers[index] || null,
          editableData: tabEditableData,
          validationErrors: null,
          taricJsonErrors: null,
          isDirty: false,
          status: 'draft',
          createdAt: Date.now(),
          label: `Export #${nextIndex}`,
        };
        newTabOrder.push(tabId);
        nextIndex++;
      });

      return {
        ...state,
        tabs: newTabs,
        tabOrder: newTabOrder,
        activeTabId: null, // Default to "All" view (null = show all tabs)
        nextTabIndex: nextIndex,
      };
    }

    case RESET_TABS:
      return initialTabsState;

    default:
      return state;
  }
};

// Action Creators
export const createTab = (tabId, declarationIndex, initialData) => ({
  type: CREATE_TAB,
  payload: { tabId, declarationIndex, initialData },
});

export const deleteTab = (tabId) => ({
  type: DELETE_TAB,
  payload: { tabId },
});

export const switchTab = (tabId) => ({
  type: SWITCH_TAB,
  payload: { tabId },
});

export const updateTabOrder = (newOrder) => ({
  type: UPDATE_TAB_ORDER,
  payload: { newOrder },
});

export const updateTabEditableData = (tabId, data) => ({
  type: UPDATE_TAB_EDITABLE_DATA,
  payload: { tabId, data },
});

export const updateTabValidationErrors = (tabId, errors) => ({
  type: UPDATE_TAB_VALIDATION_ERRORS,
  payload: { tabId, errors },
});

export const updateTabTaricErrors = (tabId, errors) => ({
  type: UPDATE_TAB_TARIC_ERRORS,
  payload: { tabId, errors },
});

export const updateTabSelectedAgent = (tabId, agent) => ({
  type: UPDATE_TAB_SELECTED_AGENT,
  payload: { tabId, agent },
});

export const updateTabSelectedSender = (tabId, sender) => ({
  type: UPDATE_TAB_SELECTED_SENDER,
  payload: { tabId, sender },
});

export const updateTabSelectedReceiver = (tabId, receiver) => ({
  type: UPDATE_TAB_SELECTED_RECEIVER,
  payload: { tabId, receiver },
});

export const updateTabStatus = (tabId, status) => ({
  type: UPDATE_TAB_STATUS,
  payload: { tabId, status },
});

export const updateTabIsDirty = (tabId, isDirty) => ({
  type: UPDATE_TAB_IS_DIRTY,
  payload: { tabId, isDirty },
});

export const migrateArrayToTabs = (extractedData, selectedAgents, selectedSenders, selectedReceivers, editableData) => ({
  type: MIGRATE_ARRAY_TO_TABS,
  payload: { extractedData, selectedAgents, selectedSenders, selectedReceivers, editableData },
});

export const resetTabs = () => ({
  type: RESET_TABS,
});

export default tabsReducer;

